import { useEffect, useMemo, type ReactNode } from "react";
import i18n from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";

import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  btp,
  colp,
  common,
  met,
  workflowVersionToLanguage,
} from "@thalamos/vision-common";

export const resources = {
  en: {
    translation: common,
  },
  enMet: {
    translation: met,
  },
  enBtp: {
    translation: btp,
  },
  enColp: {
    translation: colp,
  },
};

const config = {
  resources,
  ns: ["translation"],
  defaultNS: "translation",
  lng: "en",
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
};

void i18n.use(initReactI18next).init(config);

/**
 * A React component which wraps its children in an i18n provider
 * scoped to the locale for the given workflow version.
 *
 * This is mostly useful for Storybook stories and testing when we
 * know the workflow version and want to ensure the correct locale
 * is used for translations.
 */
export const WorkflowLocale = ({
  workflowVersion,
  children,
}: {
  workflowVersion: WorkflowVersion;
  children: ReactNode;
}) => {
  // Figure out the default locale for the workflow version
  const locale = workflowVersionToLanguage[workflowVersion];

  // Make a new i18n instance which stays in sync with the desired locale
  const scopedI18n = useMemo(() => i18n.cloneInstance(), []);
  useEffect(() => {
    if (scopedI18n.language !== locale) {
      void scopedI18n.changeLanguage(locale);
    }
  }, [locale, scopedI18n]);

  // Wrap the children in the i18n provider
  return <I18nextProvider i18n={scopedI18n}>{children}</I18nextProvider>;
};

export default i18n;
